<script setup>
import { ref, reactive, onMounted, computed, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  SearchOutlined,
  CheckOutlined,
  CheckCircleFilled,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DownloadOutlined,
  InboxOutlined,
  ClusterOutlined,
  DownOutlined,
} from '@ant-design/icons-vue'
import FindItem from './find-item.vue'
import {
  getAlgorithmList,
  getChipset,
  submitAlgorithmContribution,
} from '@/api/image'
import { debounce } from '@/utils/utils'
import { setTrack } from '@/api/login'
import { requireLoginSession } from '@/utils/experience'
import {
  normalizeAlgorithmPublicAssetUrls,
  resolveApiAssetUrl,
} from '@/utils/algorithmPublicAssets'

// 基础配置（保持原样）
const PAGE_SIZE = 9
const DEFAULT_ALGORITHM_COVER = `${import.meta.env.BASE_URL}image/market-redesign/algorithm-placeholder.svg`
const CONTRIBUTION_DEMO_URL = `${import.meta.env.BASE_URL}download/resnet18.zip`

const handleCoverError = (event) => {
  const image = event.currentTarget
  if (image.getAttribute('src') !== DEFAULT_ALGORITHM_COVER) {
    image.src = DEFAULT_ALGORITHM_COVER
  }
}

// 状态管理与路由（保持原样）
const store = useStore()
const router = useRouter()
const route = useRoute()

// 响应式数据（保持原样）
const data = ref([])
const total = ref(0)
const hasNextPage = ref(false)
const loading = ref(false)
let listRequestId = 0
let isUnmounted = false
const searchQuery = ref('')
const selectedTags = ref([])
const selectedCategory = ref([])
const collapsedCategories = reactive(new Set())
const toggleCategory = (type) => {
  if (collapsedCategories.has(type)) collapsedCategories.delete(type)
  else collapsedCategories.add(type)
}
const selectedModule = ref([])
const activeKey = ref(['1'])
const chipList = ref([])
const contributionModalOpen = ref(false)
const contributionCurrent = ref(0)
const contributionFormRef = ref(null)
const contributionSubmitting = ref(false)
const contributionSubmitted = ref(false)

// 请求参数（保持原样）
const queryParams = ref({
  page: 1,
  page_size: PAGE_SIZE,
  search: '',
  types: '',
  // 创建时间相同时用唯一 ID 保证分页顺序稳定。
  sort: 'created_date,id',
  chipset: '',
  category__type: '',
})

const chipsetParams = ref({
  page: 1,
  page_size: 10,
})

// 计算属性（保持原样）
const list = computed(() => store.getters.algorithmList)
const hasMoreData = computed(
  () => hasNextPage.value && !loading.value,
)
const isEmptyResult = computed(() => total.value === 0 && !loading.value)

const contributionSteps = [
  { title: '基本信息' },
  // { title: '技术规格' },
  { title: '上传提交' },
]

const contributionStepFields = [
  ['name', 'version', 'description'],
  // [
  //   'computePlatform',
  //   'framework',
  //   'modelFormat',
  //   'chipPlatforms',
  // ],
  ['modelFiles', 'email', 'agreement'],
]

const frameworkOptions = [
  { label: 'TensorFlow', value: 'TensorFlow' },
  { label: 'PyTorch', value: 'PyTorch' },
  { label: 'ONNX Runtime', value: 'ONNX Runtime' },
  { label: 'TFLite', value: 'TFLite' },
  { label: 'OpenVINO', value: 'OpenVINO' },
  { label: '自研框架', value: '自研框架' },
]

const modelFormatOptions = [
  { label: 'ONNX', value: 'ONNX' },
  { label: 'TFLite', value: 'TFLite' },
  { label: 'SNPE DLC', value: 'SNPE DLC' },
  { label: 'QNN', value: 'QNN' },
  { label: 'MNN', value: 'MNN' },
  { label: 'TensorRT', value: 'TensorRT' },
  { label: '其他', value: '其他' },
]

const computePlatformOptions = [
  { label: 'x86 GPU', value: 'x86 GPU' },
  { label: 'x86 CPU', value: 'x86 CPU' },
  { label: 'ARM CPU', value: 'ARM CPU' },
  { label: 'NPU', value: 'NPU' },
  { label: '其他', value: '其他' },
]

const chipPlatformGroups = [
  {
    platform: 'Qualcomm',
    modules: [
      { label: 'SG560D', value: 'SG560D' },
      { label: 'SC200U', value: 'SC200U' },
      { label: 'SC200V', value: 'SC200V' },
      { label: 'SC668S', value: 'SC668S' },
      { label: 'SC680A', value: 'SC680A' },
    ],
  },
  {
    platform: 'Rockchip',
    modules: [
      { label: 'SC362Z', value: 'SC362Z' },
      { label: 'SC368Z', value: 'SC368Z' },
    ],
  },
  {
    platform: 'Unisoc',
    modules: [{ label: 'SG530C', value: 'SG530C' }],
  },
]

const defaultChipPlatformGroups = chipPlatformGroups.map(
  (group) => group.platform,
)

const createContributionForm = () => ({
  name: '',
  version: '1.0.0',
  description: '',
  email: '',
  modelFiles: [],
  agreement: false,
})

const contributionForm = reactive(createContributionForm())
const contributionFiles = reactive({
  model: [],
})
const openChipPlatformGroups = ref([...defaultChipPlatformGroups])

const contributionRules = {
  name: [{ required: true, message: '请输入算法名称', trigger: 'blur' }],
  version: [{ required: true, message: '请输入算法版本', trigger: 'blur' }],
  email: [
    { required: true, message: '请输入联系邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' },
  ],
  description: [{ required: true, message: '请输入算法描述', trigger: 'blur' }],
  // 技术规格暂时隐藏，恢复三步流程时再打开以下校验。
  // computePlatform: [
  //   { required: true, message: '请选择推理平台', trigger: 'change' },
  // ],
  // framework: [{ required: true, message: '请选择推理框架', trigger: 'change' }],
  // modelFormat: [
  //   { required: true, message: '请选择模型格式', trigger: 'change' },
  // ],
  // chipPlatforms: [
  //   {
  //     required: true,
  //     type: 'array',
  //     min: 1,
  //     message: '请选择平台与模组',
  //     trigger: 'change',
  //   },
  // ],
  modelFiles: [
    {
      required: true,
      type: 'array',
      min: 1,
      message: '请上传模型文件',
      trigger: 'change',
    },
  ],
  agreement: [
    {
      validator: (_, value) =>
        value
          ? Promise.resolve()
          : Promise.reject(new Error('请先阅读并同意贡献协议')),
      trigger: 'change',
    },
  ],
}

// 路由参数同步（保持原逻辑）
watch(
  () => route.query,
  (newQuery) => {
    const { category, types, chipset, search } = newQuery
    searchQuery.value = search || ''
    selectedCategory.value = category ? [category] : []
    selectedTags.value = types ? [types] : []
    selectedModule.value = chipset
      ? chipset.split(',').map((item) => item.trim())
      : []
  },
  { immediate: true },
)

const refreshList = debounce(() => {
  if (!isUnmounted) getList(true)
}, 300)

// 同时监听已提交的搜索词，输入草稿只在 Enter / 搜索按钮 / 清空后提交。
watch(
  [
    () => selectedTags.value,
    () => selectedCategory.value,
    () => selectedModule.value,
    () => route.query.search,
  ],
  refreshList,
  { deep: true },
)

// bfcache防护：页面隐藏时清理资源（新增）
const handleVisibilityChange = () => {
  if (document.hidden) {
    // 若后续添加WebSocket/IndexedDB，在此处关闭
  }
}

// 组件生命周期（保持原逻辑，新增bfcache监听）
onMounted(() => {
  // 监听页面可见性，优化bfcache
  window.addEventListener('visibilitychange', handleVisibilityChange)
  // 获取数据
  getList(true)
  getChipsetData()
})

onUnmounted(() => {
  isUnmounted = true
  listRequestId += 1
  // 移除事件监听，避免内存泄漏
  window.removeEventListener('visibilitychange', handleVisibilityChange)
})

// 动态维护展开面板（保持原逻辑）
const updateActiveKey = () => {
  if (!chipList.value.length) return

  const newActiveKeys = []
  let hasAnyChecked = false

  chipList.value.forEach((panel, idx) => {
    const panelKey = (idx + 1).toString()
    const panelHasChecked = panel.children.some((val) =>
      selectedModule.value.includes(val.name),
    )

    if (panelHasChecked) {
      newActiveKeys.push(panelKey)
      hasAnyChecked = true
    }
  })

  if (!hasAnyChecked) {
    newActiveKeys.push('1')
  }

  if (JSON.stringify(newActiveKeys) !== JSON.stringify(activeKey.value)) {
    activeKey.value = newActiveKeys
  }
}

// 算法列表请求（优化参数处理和图片预加载，核心逻辑不变）
const getList = async (reset = false) => {
  if (loading.value && !reset) return
  const requestId = ++listRequestId
  loading.value = true
  try {
    if (reset) {
      queryParams.value.page = 1
      data.value = []
      total.value = 0
      hasNextPage.value = false
    }
    // 整理请求参数（避免空值）
    queryParams.value = {
      ...queryParams.value,
      search: searchQuery.value.trim(),
      types: selectedTags.value.filter(Boolean).join(','),
      chipset: selectedModule.value.filter(Boolean).join(','),
      category__type: selectedCategory.value.filter(Boolean).join(','),
    }
    const requestParams = {
      ...queryParams.value,
      page: reset ? 1 : queryParams.value.page + 1,
    }
    const res = await getAlgorithmList(requestParams)
    if (requestId !== listRequestId) return
    // 处理返回数据（添加默认值，避免undefined）
    const processedItems = (res.results || []).map((item) => {
      const normalizedItem = normalizeAlgorithmPublicAssetUrls(item, item.id)
      return {
        ...normalizedItem,
        cover: normalizedItem.cover || '',
        categoryName: normalizedItem.category?.name || '未分类',
        scenario: normalizedItem.scenario?.split(',').filter(Boolean) || [],
        description: normalizedItem.description || '该算法暂无详细描述',
      }
    })
    // 同一算法跨页再次返回时只显示一次；同名但 ID 不同的算法保留。
    const seenIds = new Set()
    data.value = (reset ? processedItems : [...data.value, ...processedItems]).filter((item) => {
      if (item.id === null || item.id === undefined) return true
      const id = String(item.id)
      if (seenIds.has(id)) return false
      seenIds.add(id)
      return true
    })
    total.value = res.count || 0
    queryParams.value.page = requestParams.page
    // 是否还有下一页以接口分页信息为准，不用去重后的卡片数量判断。
    hasNextPage.value = res.next !== undefined
      ? Boolean(res.next)
      : requestParams.page * requestParams.page_size < total.value

    updateActiveKey()
  } catch (err) {
    if (requestId !== listRequestId) return
    console.error('获取算法列表失败:', err)
    // 错误处理：避免空白页面
    if (reset) {
      data.value = []
      total.value = 0
      hasNextPage.value = false
    }
  } finally {
    if (requestId === listRequestId) loading.value = false
  }
}

// 获取芯片组数据（保持原逻辑）
const getChipsetData = async () => {
  try {
    const res = await getChipset(chipsetParams.value)
    chipList.value = res || []
    updateActiveKey()
  } catch (err) {
    console.error('获取芯片组数据失败:', err)
    chipList.value = []
  }
}

// 事件处理函数（保持原逻辑，优化路由跳转）
const handleTagChange = (tag, checked) => {
  selectedTags.value = checked ? [tag.type] : []
  selectedCategory.value = []
  updateRouteQuery()
}

const handleCategoryClick = (type) => {
  const isSelected = selectedCategory.value.includes(type)
  selectedCategory.value = isSelected ? [] : [type]
  selectedTags.value = []
  updateRouteQuery()
}

const selectAllAlgorithms = () => {
  selectedCategory.value = []
  selectedTags.value = []
  updateRouteQuery()
}

const handleModuleChange = (val, checked) => {
  if (checked) {
    if (!selectedModule.value.includes(val.name)) {
      selectedModule.value = [...selectedModule.value, val.name]
    }
  } else {
    selectedModule.value = selectedModule.value.filter(
      (item) => item !== val.name,
    )
  }
  updateRouteQuery()
}

const isChipPlatformGroupOpen = (platform) =>
  openChipPlatformGroups.value.includes(platform)

const toggleChipPlatformGroup = (platform) => {
  openChipPlatformGroups.value = isChipPlatformGroupOpen(platform)
    ? openChipPlatformGroups.value.filter((item) => item !== platform)
    : [...openChipPlatformGroups.value, platform]
}

const isChipModuleSelected = (moduleValue) =>
  contributionForm.chipPlatforms.includes(moduleValue)

const toggleChipModule = (moduleValue) => {
  contributionForm.chipPlatforms = isChipModuleSelected(moduleValue)
    ? contributionForm.chipPlatforms.filter((item) => item !== moduleValue)
    : [...contributionForm.chipPlatforms, moduleValue]

  contributionFormRef.value
    ?.validateFields(['chipPlatforms'])
    .catch(() => false)
}

// 更新路由参数（保持原逻辑）
const updateRouteQuery = () => {
  const query = { ...route.query }

  if (searchQuery.value.trim()) {
    query.search = searchQuery.value.trim()
  } else {
    delete query.search
  }

  if (selectedCategory.value.length) {
    query.category = selectedCategory.value.join(',')
  } else {
    delete query.category
  }

  if (selectedTags.value.length) {
    query.types = selectedTags.value.join(',')
  } else {
    delete query.types
  }

  if (selectedModule.value.length) {
    query.chipset = selectedModule.value.join(',')
  } else {
    delete query.chipset
  }

  router.push({ path: route.path, query }, undefined, { shallow: true })
}

const updateSearchQuery = () => {
  const query = { ...route.query }

  if (searchQuery.value.trim()) {
    query.search = searchQuery.value.trim()
  } else {
    delete query.search
  }

  router.replace({ ...route, query }, undefined, { shallow: true })
  // 相同关键词再次回车也能刷新；与路由监听共享防抖，避免重复请求。
  refreshList()
}

const searchChange = (e) => {
  if (!e.target.value.trim()) updateSearchQuery()
}

const clearAllFilters = () => {
  searchQuery.value = ''
  selectedTags.value = []
  selectedCategory.value = []
  selectedModule.value = []
  router.push({ path: route.path })
}

const loadMore = () => {
  if (hasMoreData.value) {
    getList()
  }
}

const resetContributionForm = () => {
  Object.assign(contributionForm, createContributionForm())
  contributionFiles.model = []
  openChipPlatformGroups.value = [...defaultChipPlatformGroups]
  contributionCurrent.value = 0
  contributionSubmitted.value = false
}

const openContributionModal = async () => {
  if (!requireLoginSession()) return
  resetContributionForm()
  contributionModalOpen.value = true
  try {
    await setTrack({
      position: 'algorithm_market > contribute_algorithm',
    })
  } catch (error) {
    console.warn('贡献算法埋点失败:', error)
  }
}

const handleContributionCancel = () => {
  contributionModalOpen.value = false
  resetContributionForm()
}

const handleContributionFinish = () => {
  contributionModalOpen.value = false
  resetContributionForm()
}

const handleContributionAgain = () => {
  resetContributionForm()
}

const validateContributionCurrent = () => {
  const fields = contributionStepFields[contributionCurrent.value]
  return contributionFormRef.value?.validateFields(fields)
}

const handleContributionNext = async () => {
  try {
    await validateContributionCurrent()
    contributionCurrent.value += 1
  } catch (error) {
    return false
  }
}

const handleContributionPrev = () => {
  if (contributionCurrent.value > 0) contributionCurrent.value -= 1
}

const isZipContributionFile = (file) => {
  const fileName = file?.name || file?.originFileObj?.name || ''
  return fileName.toLowerCase().endsWith('.zip')
}

const beforeContributionUpload = (file) => {
  if (!isZipContributionFile(file)) {
    message.error('请上传 ZIP 格式的模型文件')
  }
  return false
}

const handleContributionFileChange = (fileKey, formKey, info, maxCount = 1) => {
  contributionFiles[fileKey] = info.fileList
    .filter((file) => isZipContributionFile(file))
    .slice(-maxCount)
  contributionForm[formKey] = contributionFiles[fileKey]
  if (contributionRules[formKey]) {
    contributionFormRef.value?.validateFields([formKey]).catch(() => false)
  }
}

const handleContributionSubmit = async () => {
  if (!requireLoginSession()) return
  contributionSubmitting.value = true
  try {
    await contributionFormRef.value.validate()

    const modelUpload = contributionFiles.model[0]
    const modelFile =
      modelUpload?.originFileObj ||
      (typeof File !== 'undefined' && modelUpload instanceof File
        ? modelUpload
        : null)
    if (!modelFile) {
      message.error('请上传 ZIP 格式的模型文件')
      return
    }

    const formData = new FormData()
    formData.append('name', contributionForm.name.trim())
    formData.append('version', contributionForm.version.trim())
    formData.append('description', contributionForm.description.trim())
    formData.append('email', contributionForm.email.trim())
    formData.append('package_file', modelFile, modelFile.name)

    await submitAlgorithmContribution(formData)
    contributionSubmitted.value = true
  } catch (error) {
    if (!error?.errorFields) {
      message.error(error?.message || '提交失败，请稍后重试')
    }
  } finally {
    contributionSubmitting.value = false
  }
}

const handleConsult = async () => {
  void setTrack({
    position: 'consultation',
  })
  location.href =
    'https://www.quectel.com.cn/contact?pro=QWxnb1N0b3JlIEFJ566X5rOV6LaF5biC&tagStr=NGExMDllYjU2NDc3OGZjZg&proType=QUnlvIDmlL7lubPlj7A'
}
</script>

<template>
  <section class="market-catalog" aria-label="算法目录">
    <div class="catalog-container">
      <div class="catalog-layout">
        <aside class="filter-header" aria-label="算法筛选">
          <div class="filter-top">
            <span>筛选条件</span>
            <button type="button" class="clear-filters" @click="clearAllFilters">
              清除全部
            </button>
          </div>
          <button
            type="button"
            class="all-algorithms"
            :class="{ 'is-selected': !selectedCategory.length && !selectedTags.length }"
            :aria-pressed="!selectedCategory.length && !selectedTags.length"
            @click="selectAllAlgorithms"
          >
            全部算法
          </button>
          <div class="catalog-filters">
            <h3 class="filter-heading">算法分类</h3>
            <section v-for="tag in list" :key="tag.type" class="category-group">
              <div class="category-header">
              <button
                type="button"
                class="category"
                :class="{ 'category-checked': selectedCategory.includes(tag.type) }"
                :aria-pressed="selectedCategory.includes(tag.type)"
                @click="handleCategoryClick(tag.type)"
              >
                <span>{{ tag.name }}</span>
              </button>
              <button
                type="button"
                class="category-toggle"
                :class="{ 'is-collapsed': collapsedCategories.has(tag.type) }"
                :aria-label="`${collapsedCategories.has(tag.type) ? '展开' : '收起'}${tag.name}`"
                :aria-expanded="!collapsedCategories.has(tag.type)"
                :aria-controls="`category-options-${tag.type}`"
                @click="toggleCategory(tag.type)"
              ><DownOutlined /></button>
              </div>
              <div v-show="!collapsedCategories.has(tag.type)" :id="`category-options-${tag.type}`" class="category-options">
                <button
                  v-for="val in tag.children"
                  :key="val.type"
                  type="button"
                  class="category-tag"
                  :class="{ 'is-selected': selectedTags.includes(val.type) }"
                  :aria-pressed="selectedTags.includes(val.type)"
                  @click="handleTagChange(val, !selectedTags.includes(val.type))"
                >
                  {{ val.name }}
                </button>
              </div>
            </section>
            <h3 class="filter-heading module-heading">平台与模组</h3>
            <a-collapse
              v-model:activeKey="activeKey"
              class="module-collapse"
              :bordered="false"
              expand-icon-position="end"
            >
              <a-collapse-panel
                v-for="(item, idx) in chipList"
                :key="(idx + 1).toString()"
                :header="item.name"
              >
                <div class="module-options">
                  <a-checkbox
                    v-for="val in item.children"
                    :key="val.name"
                    :checked="selectedModule.includes(val.name)"
                    @change="(event) => handleModuleChange(val, event.target.checked)"
                  >
                    {{ val.name }}
                  </a-checkbox>
                </div>
              </a-collapse-panel>
            </a-collapse>
          </div>
        </aside>

        <div class="right-content" :aria-busy="loading">
          <div class="catalog-toolbar">
            <a-input
              v-model:value="searchQuery"
              class="catalog-search"
              placeholder="搜索算法"
              aria-label="搜索算法"
              allow-clear
              @pressEnter="updateSearchQuery"
              @change="searchChange"
            >
              <template #prefix>
                <button
                  type="button"
                  class="search-submit"
                  aria-label="搜索"
                  @click="updateSearchQuery"
                >
                  <SearchOutlined />
                </button>
              </template>
            </a-input>
            <a-button type="primary" class="contribute-btn" @click="openContributionModal">
              即刻上传你的算法
            </a-button>
          </div>
          <p class="catalog-total" role="status">总计 {{ total }} 种结果</p>

          <div v-if="loading && !data.length" class="catalog-loading" role="status">
            <a-spin />
            <span>算法加载中...</span>
          </div>
          <a-empty v-else-if="isEmptyResult" class="catalog-empty">
            <template #description>暂无符合条件的算法，请调整筛选条件</template>
            <a-button type="primary" @click="handleConsult">联系销售定制</a-button>
          </a-empty>
          <div v-else class="card-row">
            <article v-for="(item, index) in data" :key="item.id || item.name" class="algorithm-card">
              <img
                class="card-image"
                :src="resolveApiAssetUrl(item.introduce?.cover_url || item.cover) || DEFAULT_ALGORITHM_COVER"
                @error="handleCoverError"
                :loading="index < 3 ? 'eager' : 'lazy'"
                width="360"
                height="192"
                :alt="`算法封面：${item.name}`"
              />
              <div class="card-box">
                <div class="row-title">
                  <span class="card-name">{{ item.name }}</span>
                  <a-tag class="algorithm-type">{{ item.algorithm_type?.name || item.categoryName }}</a-tag>
                </div>
                <div class="row-dec">{{ item.description }}</div>
                <div class="card-scenarios">
                  <div class="fit">适用场景</div>
                  <div class="tag-list">
                    <span v-for="val in item.application" :key="val.id || val.name" class="scenario-tag">
                      {{ val.name }}
                    </span>
                  </div>
                </div>
                <a-button
                  class="experience-button"
                  @click="router.push({
                    name: 'algorithmDetail',
                    params: { algorithmType: item.algorithm_type?.type || String(item.id) },
                    query: { id: item.id },
                  })"
                >
                  立即体验
                </a-button>
              </div>
            </article>
          </div>
          <div v-if="data.length && (hasNextPage || loading)" class="more-center">
            <a-button class="more-btn" :loading="loading" @click="loadMore">
              {{ loading ? '加载中...' : '加载更多' }}
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <FindItem />

  <a-modal
    v-model:open="contributionModalOpen"
    width="920px"
    :footer="null"
    :destroy-on-close="true"
    :mask-closable="false"
    centered
    wrapClassName="contribution-modal"
    @cancel="handleContributionCancel"
  >
    <div class="contribution-dialog">
      <template v-if="!contributionSubmitted">
        <div class="contribution-dialog__header">
          <div>
            <h3>即刻上传你的算法</h3>
            <p>
              填写以下信息提交您的 AI 算法，我们的技术团队将在 5-15
              个工作日内完成审核。
            </p>
          </div>
        </div>

        <div class="contribution-dialog__body">
          <a-steps
            v-model:current="contributionCurrent"
            :items="contributionSteps"
            size="small"
            class="contribution-steps"
          />

          <a-form
            ref="contributionFormRef"
            :model="contributionForm"
            :rules="contributionRules"
            layout="vertical"
            class="contribution-form"
          >
          <div
            v-show="contributionCurrent === 0"
            class="contribution-step-panel"
          >
            <div class="contribution-form-grid">
              <a-form-item label="算法名称" name="name" required>
                <a-input
                  v-model:value.trim="contributionForm.name"
                  placeholder="例如：二维码识别"
                />
              </a-form-item>
              <a-form-item label="算法版本" name="version" required>
                <a-input
                  v-model:value.trim="contributionForm.version"
                  placeholder="例如：1.0.0"
                />
              </a-form-item>
            </div>

            <a-form-item label="算法描述" name="description" required>
              <a-textarea
                v-model:value.trim="contributionForm.description"
                :rows="2"
                :maxlength="200"
                show-count
                placeholder="例如：通过 OCR 光学字符识别的技术快速读取二维码信息。"
              />
              <p class="field-helper">将在算法卡片和详情页首屏展示。</p>
            </a-form-item>
          </div>

          <!-- 技术规格暂时隐藏，恢复时同步打开 contributionSteps / contributionStepFields / contributionRules。
          <div
            v-show="contributionCurrent === 1"
            class="contribution-step-panel"
          >
            <div class="contribution-section-heading">
              <strong>关键指标</strong>
              <span>请填写算法在目标平台上的实际测试数据</span>
            </div>
            <div class="contribution-form-grid">
              <a-form-item label="推理平台" name="computePlatform" required>
                <a-select
                  v-model:value="contributionForm.computePlatform"
                  :options="computePlatformOptions"
                  placeholder="请选择推理平台"
                />
              </a-form-item>
              <a-form-item label="推理框架" name="framework" required>
                <a-select
                  v-model:value="contributionForm.framework"
                  :options="frameworkOptions"
                  placeholder="请选择"
                />
              </a-form-item>
              <a-form-item label="模型格式" name="modelFormat" required>
                <a-select
                  v-model:value="contributionForm.modelFormat"
                  :options="modelFormatOptions"
                  placeholder="请选择模型格式"
                />
              </a-form-item>
              <a-form-item label="输入格式" name="inputFormat">
                <a-input
                  v-model:value.trim="contributionForm.inputFormat"
                  placeholder="例如：RGB图像 640x480、YUV420、音频16kHz mono"
                />
              </a-form-item>
              <a-form-item label="输出格式" name="outputFormat">
                <a-input
                  v-model:value.trim="contributionForm.outputFormat"
                  placeholder="例如：BoundingBox + ClassLabel + Confidence JSON"
                />
              </a-form-item>
              <a-form-item label="推理时间（ms）" name="inferenceCost">
                <a-input
                  v-model:value.trim="contributionForm.inferenceCost"
                  placeholder="例如：162"
                />
              </a-form-item>
              <a-form-item label="内存占用（MB）" name="memoryUsage">
                <a-input
                  v-model:value.trim="contributionForm.memoryUsage"
                  placeholder="例如：384"
                />
              </a-form-item>
              <a-form-item label="GPU 利用率（%）" name="gpuUsage">
                <a-input
                  v-model:value.trim="contributionForm.gpuUsage"
                  placeholder="例如：1"
                />
              </a-form-item>
            </div>

            <a-form-item label="平台与模组" name="chipPlatforms" required>
              <div class="platform-module-list">
                <section
                  v-for="group in chipPlatformGroups"
                  :key="group.platform"
                  class="platform-module-group"
                >
                  <button
                    type="button"
                    class="platform-module-header"
                    :aria-expanded="isChipPlatformGroupOpen(group.platform)"
                    @click="toggleChipPlatformGroup(group.platform)"
                  >
                    <span>{{ group.platform }}</span>
                    <DownOutlined
                      class="platform-module-header__icon"
                      :class="{
                        'is-collapsed': !isChipPlatformGroupOpen(group.platform),
                      }"
                    />
                  </button>
                  <div
                    v-show="isChipPlatformGroupOpen(group.platform)"
                    class="platform-module-options"
                  >
                    <button
                      v-for="module in group.modules"
                      :key="module.value"
                      type="button"
                      class="platform-module-card"
                      :class="{
                        'is-selected': isChipModuleSelected(module.value),
                      }"
                      :aria-pressed="isChipModuleSelected(module.value)"
                      @click="toggleChipModule(module.value)"
                    >
                      <ClusterOutlined class="platform-module-card__icon" />
                      <span>{{ module.label }}</span>
                      <CheckOutlined
                        v-if="isChipModuleSelected(module.value)"
                        class="module-selected-icon"
                      />
                    </button>
                  </div>
                </section>
              </div>
            </a-form-item>
          </div>
          -->

          <div
            v-show="contributionCurrent === 1"
            class="contribution-step-panel"
          >
            <div
              class="contribution-upload-grid contribution-upload-grid--single"
            >
              <a-form-item label="上传 ZIP 工程包" name="modelFiles" required>
                <a-upload-dragger
                  :file-list="contributionFiles.model"
                  :before-upload="beforeContributionUpload"
                  :max-count="1"
                  accept=".zip"
                  @change="
                    (info) =>
                      handleContributionFileChange('model', 'modelFiles', info)
                  "
                >
                  <p class="upload-icon"><InboxOutlined /></p>
                  <p class="upload-title">点击上传 ZIP 工程包</p>
                  <p class="upload-desc">仅支持 .zip 格式，最大 2GB</p>
                </a-upload-dragger>
              </a-form-item>
            </div>

            <div class="contribution-upload-guide">
              <div class="contribution-upload-guide__header">
                <strong>工程包要求</strong>
                <a
                  class="contribution-upload-guide__download"
                  :href="CONTRIBUTION_DEMO_URL"
                  download="resnet18.zip"
                >
                  <DownloadOutlined />
                  下载示例工程包
                </a>
              </div>
              <div>
                <p>平台会在构建前校验 ZIP 包，请按以下规则准备工程文件：</p>
                <ul>
                  <li>必须是有效的 ZIP 文件，ZIP 内路径必须是相对路径。</li>
                  <li>路径不能包含空路径、<code>.</code>、<code>..</code> 或上级目录跳转。</li>
                  <li>ZIP 内必须有且只能有一个根目录，6 个必需文件放在根目录第一层。</li>
                  <li>上传包最大 2GB。</li>
                </ul>
                <div class="contribution-upload-guide__structure-title">建议结构</div>
                <pre class="contribution-upload-guide__tree">resnet18.zip
└── resnet18
    ├── README.md
    ├── convert_onnx.py
    ├── dataset_preprocess.py
    ├── dataset_postprocess.py
    ├── requirements.txt
    ├── train.py
    ├── val.py
    ├── tools
    │   ├── LabelmeDataset.py
    │   └── utils.py</pre>
              </div>
            </div>

            <a-form-item label="联系邮箱" name="email" required>
              <a-input
                v-model:value.trim="contributionForm.email"
                type="email"
                autocomplete="email"
                placeholder="例如：name@example.com"
              />
            </a-form-item>

            <div class="agreement-box">
              <h4>《移远算法超市开发者贡献协议》要点</h4>
              <ol>
                <li>
                  开发者保证所提交算法的原创性，不侵犯任何第三方知识产权。
                </li>
                <li>开发者保留算法知识产权，授权移远平台进行审核与展示。</li>
                <li>移远将对模型文件进行安全审核。</li>
                <li>双方遵守保密条款，不向第三方泄露合作细节。</li>
              </ol>
            </div>

            <a-form-item name="agreement" class="agreement-check">
              <a-checkbox v-model:checked="contributionForm.agreement">
                我已阅读并同意《移远算法超市开发者贡献协议》
              </a-checkbox>
            </a-form-item>
          </div>
          </a-form>
        </div>

        <div class="contribution-footer">
          <a-button
            v-if="contributionCurrent > 0"
            class="footer-button"
            @click="handleContributionPrev"
          >
            <ArrowLeftOutlined />
            上一步
          </a-button>
          <span v-else></span>

          <a-button
            v-if="contributionCurrent < contributionSteps.length - 1"
            type="primary"
            class="footer-button"
            @click="handleContributionNext"
          >
            下一步
            <ArrowRightOutlined />
          </a-button>
          <a-button
            v-else
            type="primary"
            class="footer-button submit-button"
            :loading="contributionSubmitting"
            @click="handleContributionSubmit"
          >
            确认提交
          </a-button>
        </div>
      </template>
      <div v-else class="contribution-success">
        <div class="contribution-success__icon">
          <CheckCircleFilled />
        </div>
        <h3>提交成功</h3>
        <p>您的算法资料已提交，我们的技术团队将在 5-15 个工作日内完成审核。</p>
        <div class="contribution-success__summary">
          <div>
            <span>算法名称</span>
            <strong>{{ contributionForm.name || '-' }}</strong>
          </div>
          <div>
            <span>当前状态</span>
            <strong>待审核</strong>
          </div>
        </div>
        <div class="contribution-success__actions">
          <a-button class="footer-button" @click="handleContributionAgain">
            继续上传算法
          </a-button>
          <a-button
            type="primary"
            class="footer-button submit-button"
            @click="handleContributionFinish"
          >
            完成
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<style lang="scss" scoped>
div {
  box-sizing: border-box;
}
.market-catalog {
  padding: 64px 0;
  background: #fff;
  color: #14233b;

  &,
  * {
    box-sizing: border-box;
  }

  button {
    font-family: inherit;
  }

  button:focus-visible {
    outline: 2px solid #ed000c;
    outline-offset: 2px;
  }
}

.catalog-container {
  width: 100%;
  max-width: 1488px;
  margin: 0 auto;
  padding: 0 24px;
}

.catalog-layout {
  display: grid;
  grid-template-columns: 288px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
}

.filter-header {
  min-width: 0;
  padding: 16px;
  border: 1px solid #dce2ea;
  border-radius: 16px;
  background: #fff;
  font-family: Stratos-Regular, 'Source Han Sans', Arial, sans-serif,
    -apple-system, BlinkMacSystemFont;
  -webkit-font-smoothing: antialiased;
}

.filter-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 500;
}

.clear-filters {
  padding: 0;
  border: 0;
  background: transparent;
  color: #ed000c;
  font-size: 14px;
  cursor: pointer;
}

.all-algorithms {
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: #f8fafc;
  color: #14233b;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.filter-heading {
  margin: 20px 0 16px;
  color: #14233b;
  font-size: 16px;
  font-weight: 500;
}

.category-group + .category-group {
  margin-top: 24px;
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.category-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #617089;
  font-size: 12px;
  cursor: pointer;

  .anticon { transition: transform 150ms ease; }
  &.is-collapsed .anticon { transform: rotate(-90deg); }
  &:focus-visible { outline: 2px solid #ed000c; outline-offset: -2px; }
}

.category {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  color: #14233b;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  span {
    overflow-wrap: anywhere;
  }

  .anticon {
    flex-shrink: 0;
    color: #617089;
    font-size: 12px;
  }
}

.category-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  justify-items: stretch;
  align-items: start;
  gap: 8px;
}

.category-tag {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  min-height: 32px;
  padding: 5px 8px;
  border: 1px solid #dce2ea;
  border-radius: 4px;
  background: #fff;
  color: #14233b;
  font-size: 14px;
  line-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
  cursor: pointer;
}

.all-algorithms.is-selected,
.category-tag.is-selected {
  background: #fff2f3;
  color: #ed000c;
}

.category:hover,
.category-checked {
  color: #ed000c;
}

.module-heading {
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.module-options {
  display: flex;
  flex-direction: column;
  gap: 12px;

  :deep(.ant-checkbox-wrapper) {
    min-width: 0;
    margin-inline-start: 0;
    color: #617089;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
}

.module-collapse {
  background: #fff;

  :deep(.ant-collapse-item) {
    border: 0;
  }

  :deep(.ant-collapse-header) {
    padding: 12px 0;
    color: #14233b;
    font-size: 14px;
  }

  :deep(.ant-collapse-content-box) {
    padding: 0 0 12px;
  }
}

.right-content {
  min-width: 0;
}

.catalog-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
}

.catalog-search {
  flex: 1;
  width: 0;
  min-width: 0;
  height: 44px;
  border-color: #dce2ea;
  border-radius: 8px;

  :deep(.ant-input) {
    min-width: 0;
    font-size: 14px;
  }

  :deep(.ant-input-prefix) {
    margin-inline-end: 8px;
  }
}

.search-submit {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #617089;
  font-size: 18px;
  cursor: pointer;
}

.catalog-toolbar .contribute-btn {
  flex: 0 0 240px;
  width: 240px;
  height: 44px;
  border-radius: 8px;
  box-shadow: none;
  font-size: 16px;
  font-weight: 500;
}

.catalog-total {
  margin: 16px 0 24px;
  color: #617089;
  font-size: 14px;
  line-height: 22px;
}

.card-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  gap: 24px;
}

.algorithm-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.card-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  height: auto;
  aspect-ratio: 360 / 192;
  object-fit: cover;
}

.card-box {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 20px;
}

.row-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.card-name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.algorithm-type {
  flex-shrink: 0;
  max-width: 50%;
  margin: 0;
  padding: 1px 6px;
  border: 1px solid #ffc9cf;
  border-radius: 4px;
  background: #fff2f3;
  color: #ed000c;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.row-dec {
  display: -webkit-box;
  height: 48px;
  overflow: hidden;
  color: #666;
  font-size: 16px;
  line-height: 24px;
  overflow-wrap: anywhere;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.fit {
  margin-bottom: 8px;
  color: #1a1a1a;
  font-size: 14px;
  font-weight: bold;
  line-height: 22px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scenario-tag {
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f5f7fa;
  color: #617089;
  font-size: 12px;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.experience-button {
  width: 100%;
  height: 40px;
  margin-top: auto;
  border: 1px solid #dce2ea;
  border-radius: 6px;
  background: #fff;
  box-shadow: none;
}

.more-center {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.more-btn {
  width: 200px;
  max-width: 100%;
  height: 44px;
  border: 1px solid #dce2ea;
  border-radius: 8px;
  background: #fff;
  box-shadow: none;
}

.catalog-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  color: #617089;
  font-size: 14px;
}

.catalog-empty {
  margin: 64px 0;
}

.contribution-dialog {
  color: #1a1a1a;
  display: flex;
  flex-direction: column;
}

.contribution-dialog__header {
  flex: 0 0 auto;
  padding: 0 8px 16px 0;

  h3 {
    margin: 0 0 6px;
    font-size: 20px;
    line-height: 1.3;
    font-weight: 600;
    color: #111827;
  }

  p {
    margin: 0;
    color: #667085;
    line-height: 1.6;
    font-size: 14px;
  }
}

.contribution-steps {
  flex: 0 0 auto;
  padding: 8px 4px 18px;
  background: #fff;
}

.contribution-form {
  flex: 1;
  padding: 12px 4px 0;
}

.contribution-dialog__body {
  max-height: min(66vh, 560px);
  padding-right: 8px;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: #cbd5e1;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.contribution-step-panel {
  min-height: 0;
}

.contribution-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
}

.contribution-section-heading {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 2px 0 8px;

  strong {
    color: #111827;
    font-size: 15px;
    font-weight: 600;
  }

  span {
    color: #98a2b3;
    font-size: 12px;
  }
}

.contribution-upload-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
}

.contribution-upload-grid--single {
  grid-template-columns: 1fr;
}

.field-helper {
  margin: 6px 0 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.5;
}

.platform-module-list {
  display: grid;
  grid-template-columns: 1.35fr 1fr 1fr;
  gap: 12px;
  width: 100%;
}

.platform-module-group {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fbfcfe;
}

.platform-module-header {
  width: 100%;
  min-height: 30px;
  padding: 0 2px 8px;
  border: 0;
  background: transparent;
  color: #111827;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
}

.platform-module-header__icon {
  color: #344054;
  font-size: 12px;
  transition: transform 0.18s ease;
}

.platform-module-header__icon.is-collapsed {
  transform: rotate(-90deg);
}

.platform-module-options {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.platform-module-card {
  width: 100%;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #d9dee7;
  border-radius: 4px;
  background: #fff;
  color: #111827;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 1.3;
  text-align: left;
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease;
}

.platform-module-card:hover {
  border-color: rgba(237, 0, 12, 0.45);
  background: #fffafa;
}

.platform-module-card:focus-visible {
  outline: 2px solid rgba(237, 0, 12, 0.24);
  outline-offset: 2px;
}

.platform-module-card.is-selected {
  border-color: #ed000c;
  background: rgba(237, 0, 12, 0.05);
  box-shadow: inset 0 0 0 1px rgba(237, 0, 12, 0.1);
}

.platform-module-card__icon {
  color: #111827;
  font-size: 15px;
  flex-shrink: 0;
}

.platform-module-card.is-selected .platform-module-card__icon,
.module-selected-icon {
  color: #ed000c;
}

.module-selected-icon {
  margin-left: auto;
  font-size: 13px;
  flex-shrink: 0;
}

.upload-icon {
  margin: 0 0 4px;
  color: #667085;
  font-size: 22px;
}

.upload-title {
  margin: 0;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
}

.upload-desc {
  margin: 4px 0 0;
  color: #98a2b3;
  font-size: 11px;
  line-height: 1.4;
}

.contribution-upload-guide {
  margin: -2px 0 14px;
  padding: 14px 16px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f8fafc;
  color: #667085;
  font-size: 12px;
  line-height: 1.65;

  p {
    margin: 6px 0 4px;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  li {
    margin: 1px 0;
  }

  code {
    padding: 1px 4px;
    border-radius: 3px;
    background: #eef2f6;
    color: #475467;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      'Liberation Mono', monospace;
  }
}

.contribution-upload-guide__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  strong {
    color: #111827;
    font-size: 14px;
    font-weight: 600;
  }
}

.contribution-upload-guide__download {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  color: #1677ff;
  font-size: 12px;
  text-decoration: none;

  &:hover {
    color: #0958d9;
  }
}

.contribution-upload-guide__structure-title {
  margin-top: 8px;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
}

.contribution-upload-guide__tree {
  margin: 8px 0 0;
  padding: 9px 12px;
  overflow-x: auto;
  border-radius: 6px;
  background: #eef2f6;
  color: #475467;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}

.agreement-box {
  padding: 14px 18px;
  border: 1px solid #d9dee7;
  border-radius: 8px;
  background: #f8fafc;
  color: #667085;

  h4 {
    margin: 0 0 12px;
    color: #111827;
    font-size: 15px;
    font-weight: 600;
  }

  ol {
    margin: 0;
    padding-left: 18px;
    line-height: 1.65;
  }
}

.agreement-check {
  margin-top: 10px;
}

.contribution-footer {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin: 8px -26px 0;
  padding: 14px 26px 18px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

.footer-button {
  min-width: 104px;
  height: 40px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.submit-button {
  min-width: 116px;
}

.contribution-success {
  display: flex;
  min-height: 430px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 20px 28px;
  text-align: center;

  h3 {
    margin: 18px 0 8px;
    color: #111827;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.35;
  }

  p {
    max-width: 460px;
    margin: 0;
    color: #667085;
    font-size: 14px;
    line-height: 1.7;
  }
}

.contribution-success__icon {
  display: inline-flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #12b76a;
  background: #ecfdf3;
  font-size: 40px;
}

.contribution-success__summary {
  display: grid;
  width: min(460px, 100%);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 28px 0 24px;

  div {
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f8fafc;
    text-align: left;
  }

  span {
    display: block;
    margin-bottom: 5px;
    color: #98a2b3;
    font-size: 12px;
  }

  strong {
    display: block;
    overflow: hidden;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.contribution-success__actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

:deep() {
  .contribution-form {
    .ant-form-item {
      margin-bottom: 6px;
    }

    .ant-form-item-label > label {
      color: #111827;
      font-weight: 600;
      height: 22px;
      font-size: 14px;
    }

    .ant-form-item-control {
      min-height: 60px;
      position: relative;
      padding-bottom: 18px;
    }

    .ant-form-item-explain {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      min-height: 18px;
      font-size: 12px;
      line-height: 18px;
    }

    .ant-form-item-explain-error {
      min-height: 18px;
    }

    .ant-input,
    .ant-select-selector,
    .ant-input-affix-wrapper,
    .ant-picker {
      border-radius: 6px;
    }

    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
      background: #fff;
      border-color: #d9dee7;
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        background 0.2s ease;
    }

    .ant-input:not(textarea),
    .ant-select-selector {
      min-height: 40px;
    }

    textarea.ant-input {
      min-height: auto;
      line-height: 1.55;
      padding-top: 10px;
      padding-bottom: 10px;
    }

    .ant-input:hover,
    .ant-input-affix-wrapper:hover,
    .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
      border-color: #aab2c0;
    }

    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper-focused,
    .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
      border-color: #ed000c;
      box-shadow: 0 0 0 3px rgba(237, 0, 12, 0.08);
    }

    .ant-select-single .ant-select-selector {
      align-items: center;
    }

    .ant-select-single .ant-select-selector .ant-select-selection-item,
    .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
      display: flex;
      align-items: center;
      line-height: 1;
    }

    .ant-select-single .ant-select-selector .ant-select-selection-search {
      height: 40px;
    }

    .ant-select-single .ant-select-selector .ant-select-selection-search-input {
      height: 40px;
      line-height: 40px;
    }

    .ant-upload-wrapper .ant-upload-drag {
      border-color: #d9dee7;
      border-radius: 8px;
      background: #f8fafc;
      min-height: 78px;
      padding: 10px 12px;
      transition:
        border-color 0.2s ease,
        background 0.2s ease;
    }

    .ant-upload-wrapper .ant-upload-drag:not(.ant-upload-disabled):hover {
      border-color: #ed000c;
      background: rgba(237, 0, 12, 0.03);
    }
  }

  .contribution-steps.ant-steps {
    .ant-steps-item-icon {
      width: 26px;
      height: 26px;
      margin-inline-start: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      border: 1px solid #dfe3e8;
      background: #fff;
      color: #8b929c;
      font-weight: 600;
      font-size: 12px;
    }

    .ant-steps-item-icon > .ant-steps-icon {
      position: static;
      top: auto;
      display: block;
      line-height: 1;
    }

    .ant-steps-item-process .ant-steps-item-icon {
      border-color: #ed000c;
      background: #ed000c;
      box-shadow: 0 3px 8px rgba(237, 0, 12, 0.18);
    }

    .ant-steps-item-finish .ant-steps-item-icon {
      border-color: rgba(237, 0, 12, 0.24);
      background: rgba(237, 0, 12, 0.06);
    }

    .ant-steps-item-finish .ant-steps-icon {
      color: #ed000c;
    }

    .ant-steps-item-title {
      color: #8b929c;
      font-weight: 500;
      font-size: 14px;

      &::after {
        display: block;
        height: 1px;
        background: repeating-linear-gradient(
          90deg,
          #dfe3e8 0,
          #dfe3e8 4px,
          transparent 4px,
          transparent 8px
        );
      }
    }

    .ant-steps-item:last-child .ant-steps-item-title::after {
      display: none;
    }

    .ant-steps-item-process .ant-steps-item-title {
      color: #111827;
      font-weight: 600;
    }

    .ant-steps-item-finish .ant-steps-item-title {
      color: #4b5563;

      &::after {
        background: repeating-linear-gradient(
          90deg,
          rgba(237, 0, 12, 0.32) 0,
          rgba(237, 0, 12, 0.32) 4px,
          transparent 4px,
          transparent 8px
        );
      }
    }

    .ant-steps-item-tail {
      display: none;
    }
  }
}
</style>

<style lang="scss" scoped>
@media (max-width: 1199px) {
  .card-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .catalog-layout {
    grid-template-columns: 240px minmax(0, 1fr);
  }

  .card-row {
    grid-template-columns: 1fr;
  }

  .catalog-toolbar {
    flex-wrap: wrap;
  }

  .catalog-search {
    flex-basis: 100%;
    width: 100%;
  }

  .catalog-toolbar .contribute-btn {
    flex-basis: 100%;
    width: 100%;
  }
}

@media (max-width: 767px) {
  .market-catalog {
    padding: 40px 0;
  }

  .catalog-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .filter-header {
    width: 100%;
  }
}

@media (max-width: 940px) {
  .contribution-form-grid {
    grid-template-columns: 1fr;
  }

  .contribution-upload-grid {
    grid-template-columns: 1fr;
  }

  .platform-module-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .contribution-dialog {
    min-height: auto;
  }

  .contribution-dialog__body {
    max-height: calc(100vh - 220px);
  }

  .contribution-dialog__header {
    padding-bottom: 16px;

    h3 {
      font-size: 20px;
    }
  }

  .contribution-upload-guide__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .contribution-steps {
    padding-bottom: 16px;
  }

  .contribution-step-panel {
    min-height: auto;
  }

  .platform-module-list {
    grid-template-columns: 1fr;
  }

  .contribution-footer {
    margin: 8px -16px 0;
    padding: 12px 16px 16px;
  }

  .contribution-success {
    min-height: 360px;
    padding: 28px 4px 18px;
  }

  .contribution-success__summary {
    grid-template-columns: 1fr;
  }

  .contribution-success__actions {
    width: 100%;
    flex-direction: column;

    .footer-button {
      width: 100%;
    }
  }
}

:deep() {
  .ant-empty-description {
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.contribution-modal {
  .ant-modal {
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    padding: 24px 26px 0;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  }

  .ant-modal-close {
    top: 18px;
    right: 18px;
  }

  .ant-modal-body {
    padding: 0;
  }
}

@media (max-width: 640px) {
  .contribution-modal {
    .ant-modal {
      max-width: calc(100vw - 20px);
      margin: 10px auto;
    }

    .ant-modal-content {
      padding: 24px 16px 0;
    }
  }
}
</style>
